const _ = require("lodash");
const moment = require("moment");
const async = require("async");
const {fieldPathJoinGetter} = require("../utils/kp3Utils");

export const findStoredItem = (node, id, seen = new WeakSet()) => {
    if(!node || typeof node !== "object" || seen.has(node)) return undefined;
    seen.add(node);
    if(_.isArray(node)) {
        const found = node.find(item => item && item.id === id);
        if(found) return found;
    }
    for(const childKey of Object.keys(node)) {
        const found = findStoredItem(node[childKey], id, seen);
        if(found) return found;
    }
};




const codeNameFields = function({codeUniqueWith, codeValidator, nameOptional, uniqueName, codeTKey, nameTKey}) {
    return [
        {
            path: "code",
            ps: {object: _.compact([codeValidator])},
            index: true,
            unique: true,
            notEmpty: true,
            uniqueWith: codeUniqueWith,
            tKey: codeTKey
        },
        {path: "name", tKey: nameTKey, unique: uniqueName, notEmpty: !nameOptional},
        fieldPathJoinGetter({path: "fullName", fieldPath: ["code", "name"]})
    ]
};

const firstLastNameFields = [
    {path: "firstName", ps: {object: ["notEmpty"]}},
    {path: "lastName", ps: {object: ["notEmpty"]}},
    {path: "name", f: function() { return this.firstName + " " + this.lastName; }}
];

const descriptionField = {path: "description", long: true};

// Stored children come from context.businessOldObject with the parent back-link applied
// (see addParentLinkOnChildren). Re-inserting them as-is nests the old parent inside the
// new document and creates a cycle, so drop any property pointing at that parent.
const withoutParentLink = (stored, parent) => _.omitBy(stored, value => value === parent);

const commentsField = {
    path: "comments",
    link: "OTM",
    fieldPath: ["id", "text", "date", "user.id", "user.name"],
    ps: {object: [function(comments) {
        // Comments are fully append-only: an already persisted comment can be
        // neither edited nor removed through a forged request (the client only
        // ever appends). So every comment carrying a known id is replaced by its
        // stored version, and any stored comment dropped from the payload is
        // re-appended. Only brand new comments (no matching id) pass through.
        if(! this.options.$v) return comments; // not the authoritative save pass
        const fieldPath = this.norm && this.norm.path; // the host field name, e.g. "comments"
        const oldComments = fieldPath && _.get(this.options.context, ["businessOldObject", fieldPath]);
        if(! _.isArray(oldComments) || ! oldComments.length) return comments;
        const oldById = _.keyBy(oldComments.filter(c => c && c.id), "id");
        const parent = this.options.context.businessOldObject;
        const next = (_.isArray(comments) ? comments : []).map(c => (c && c.id && oldById[c.id]) ? withoutParentLink(oldById[c.id], parent) : c);
        const presentIds = new Set(_.compact(next.map(c => c && c.id)));
        for(const old of oldComments) {
            if(old && old.id && !presentIds.has(old.id)) next.push(withoutParentLink(old, parent));
        }
        return next;
    }]}
};

const conversationField = {
    path: "conversations",
    link: "OTM",
    //fieldPath: ['id', 'text', 'sender.id', 'sender.name', 'seenBy', 'files', "responseTo", "parentMessage", "timestamp", "from"]
    ps: {object: [function(conversations) {
        // Once a message is persisted, only seenBy (read receipts) and files
        // (renaming a file on an already-sent message) are meant to change,
        // and messages can be deleted. So every message carrying a known id
        // is replaced by its stored version except for those two fields,
        // which are kept from the incoming payload; missing ids are not
        // re-appended (unlike comments) since deletion is a real feature here.
        if(!this.options.$v) return conversations; // not the authoritative save pass
        const fieldPath = this.norm && this.norm.path; // the host field name, e.g. "conversations"
        const oldConversations = fieldPath && _.get(this.options.context, ["businessOldObject", fieldPath]);
        if(!_.isArray(oldConversations) || !oldConversations.length) return conversations;
        const oldById = _.keyBy(oldConversations.filter(m => m && m.id), "id");
        return (_.isArray(conversations) ? conversations : []).map(m => {
            const old = m && m.id && oldById[m.id];
            if(! old) return m;
            // files: keep the stored files (no add/remove/other-property changes),
            // only carrying over a renamed filename for ids that already existed.
            const newFilesById = _.keyBy(_.isArray(m.files) ? m.files : [], "id");
            const files = (_.isArray(old.files) ? old.files : []).map(f => {
                const newFile = f && f.id && newFilesById[f.id];
                return newFile ? {...f, filename: newFile.filename} : f;
            });
            return {...withoutParentLink(old, this.options.context.businessOldObject), seenBy: m.seenBy, files};
        });
    }]}
};

const filesField = {
    path: "files",
    link: "OTM",
    fieldPath: ["id", "filename", "date", "password", "tags"],
    ps: {object: [function(files) {
        // Once a file is persisted, only filename (rename) and tags stay
        // client-editable; date and password are locked to their stored
        // value. Files can be deleted (dropped ids are not re-appended,
        // unlike comments) since that's a real feature for this facet.
        // This field is also used in OTO mode (a single file, not a list),
        // so both a single object and an array have to be handled here.
        if(! this.options.$v) return files; // not the authoritative save pass
        const fieldPath = this.norm && this.norm.path; // the host field name, e.g. "files"
        const oldValue = fieldPath && _.get(this.options.context, ["businessOldObject", fieldPath]);
        const parent = this.options.context.businessOldObject;
        const carryOver = (f, old) => old ? {...withoutParentLink(old, parent), filename: f.filename, tags: f.tags} : f;

        if(!_.isArray(files)) {
            const matches = files && _.isPlainObject(oldValue) && oldValue.id === files.id;
            return matches ? carryOver(files, oldValue) : files;
        }

        if(!_.isArray(oldValue) || !oldValue.length) return files;
        const oldById = _.keyBy(oldValue.filter(f => f && f.id), "id");
        return files.map(f => carryOver(f, f && f.id && oldById[f.id]));
    }]}
};

const dateField = {path: "date", type: "date"};

const commentEntity = {
    type: "mongoInternal",
    subType: "Comment", //kludge
    fields: [
        {path: "text"},
        {path: "date",
            ps: {object: [function(date, comment) {
            // Always stamped from the server clock on the authoritative save
            // pass, never trusting a client-supplied value. Harmless on edits
            // too, since the append-only guard on the host `comments` field
            // (see commentsField above) discards it in favor of the stored date.
            if(!date) date = moment().format("YYYY-MM-DD HH:mm:ss");
            if (this.options.$v) {
                const businessOldObject = this.options.context && this.options.context.businessOldObject;
                const storedComment = comment?.id && businessOldObject && findStoredItem(businessOldObject, comment.id);
                if (storedComment?.date) date = moment(storedComment.date).format("YYYY-MM-DD HH:mm:ss")
            }
            return date;
            }]}
        },
        {path: "user", type: "object", nullable: true,
            ps: {object: [function(user, comment) {
            // Derive the author from the authenticated session on creation, never
            // trust the client value. Existing comments are frozen upstream by
            // the append-only guard on the host `comments` field.
            const contextUser = this.options.context && this.options.context.user;
            if(!contextUser || !this.options.$v) return user;
            const oldObject = this.options.context?.businessOldObject;
            const storedComment = comment && comment.id && oldObject && findStoredItem(oldObject, comment.id,);
            if(storedComment && storedComment.user) return storedComment.user;
            return {id: contextUser.id, name: contextUser.name};
            }]}
        }
    ]
};

const conversationEntity = {
    type: "mongoInternal",
    subType: "Conversation",
    fields: [
        {path: "text"},
        {path: 'sender', type: "object", nullable: true, ps: {object: [function(sender, message) {
            // Sender comes from the authenticated session, never from the client.
            // An already persisted message keeps its stored sender, found by id since
            // the host `conversations` guard can't always locate the old array.
            const contextUser = this.options.context && this.options.context.user;
            if(!contextUser || !this.options.$v) return sender;
            const oldObject = this.options.context.businessOldObject;
            const storedMessage = message && message.id && oldObject && findStoredItem(oldObject, message.id);
            if(storedMessage && storedMessage.sender) return storedMessage.sender;
            return {id: contextUser.id, name: contextUser.name};
        }]}},
        {path: "responseTo", type: 'object', nullable: true},
        {path: "parentMessage", type: 'object', nullable: true},
        {path: "seenBy", list: true},
        {path: "files", list: true, type: 'object'},
        {path: "timestamp", ps: {object: [function(timestamp, message) {
            // Never trust a client-supplied timestamp. On the authoritative save pass
            // an already persisted message keeps its stored timestamp (found by id
            // wherever its host `conversations` array sits in the old object);
            // anything else is stamped from the server clock. ISO format to match
            // what the client itself writes (see conversation2.js).
            const now = () => new Date().toISOString();
            if(!this.options.$v) return timestamp || now();
            const oldObject = this.options.context && this.options.context.businessOldObject;
            const storedMessage = message && message.id && oldObject && findStoredItem(oldObject, message.id);
            return (storedMessage && storedMessage.timestamp) || now();
        }]}},
        {path: "from"}
    ]
};

const fileEntity = {
    type: "mongoInternal",
    subType: "File", //kludge
    fields: [
        {path: "filename"},
        {path: "date", ps: {object: [function(date, file) {
            // Never trust a client-supplied date. On the authoritative save pass an
            // already persisted file keeps its stored date (found by id wherever its
            // host `files` array sits in the old object); anything else is stamped
            // from the server clock.
            if(!this.options.$v) return date || moment().format("YYYY-MM-DD HH:mm");
            const oldObject = this.options.context && this.options.context.businessOldObject;
            const storedFile = file && file.id && oldObject && findStoredItem(oldObject, file.id);
            return (storedFile && storedFile.date) || moment().format("YYYY-MM-DD HH:mm");
        }]}},
        {path: "user", type: "object", nullable: true, ps: {object: [function(user, file) {
            // Author comes from the authenticated session, never from the client.
            // An already persisted file keeps its stored author, found by id since
            // the host `files` guard can't always locate the old array.
            const contextUser = this.options.context && this.options.context.user;
            if(!contextUser || !this.options.$v) return user;
            const oldObject = this.options.context?.businessOldObject;
            const storedFile = file && file.id && oldObject && findStoredItem(oldObject, file.id);
            if(storedFile && storedFile.user) return storedFile.user;
            return {id: contextUser.id, name: contextUser.name};
        }]}},
        {path: "password"},
        {path: "tags", list: true, type: "object", nullable: true},
    ],
    afterSave(newObject, oldObject, callback) {
        console.log('afterSave')
        callback()
    }
};

export default [
    {name: "codeName", norm: {fields: [
        {path: "fields", default: [], $p: {$u: function(fields) {
            return _.cloneDeep(codeNameFields(this.options.facet)).concat(fields);
        }}}
    ]}},
    {name: "firstLastName", norm: {fields: [
        {path: "fields", default: [], $p: {$u: function(fields) {
            return _.cloneDeep(firstLastNameFields).concat(fields);
        }}}
    ]}},
    {name: "translatedField", norm: {fields: [
        {path: "fields", default: [], $p: {$u: function(fields) {

            // Field translation from field are typically used object names
            // we typically specify in the field name things like :
            //  en:: name
            //  fr:: shop
            const translateFromField = (object, context) => {
                const referenceField = this.options.facet.referenceField || "name";
                const translate = context.translateName || _.identity;
                return translate(object[referenceField]);
            };

            return [{
                path: `t${this.options.facet.referenceField ? _.upperFirst(this.options.facet.referenceField) : 'Name'}`,
                $f: (object, context, callback) => {
                    callback(null, translateFromField(object, context));
                }
            }].concat(fields);
        }}}
    ]}},
    {name: "description", norm: {fields: [
        {path: "fields", default: [], $p: {$u: function(fields) {
            return [_.cloneDeep(descriptionField)].concat(fields);
        }}}
    ]}},
    {name: "comments", norm: {fields: [
        {path: "fields", default: [], $p: {$f: function(fields, entity, entities) {
            const commentEntityName = this.options.facet.entityName || ("Comment" + entity.name);

            const thisCommentEntity = _.cloneDeep(commentEntity);
            thisCommentEntity.name = commentEntityName;
            thisCommentEntity.model = entity.model;

            const thisCommentsField = _.cloneDeep(commentsField);
            thisCommentsField.type = commentEntityName;

            entities.push(thisCommentEntity);
            fields.push(thisCommentsField);
        }}}
    ]}},
    {
        name: "conversations",
        norm: {
            fields: [
                {
                    path: "fields",
                    default: [],
                    $p: {
                        $f: function(fields, entity, entities) {
                            const conversationEntityName = this.options.facet.entityName || ("Conversation" + entity.name);

                            const thisConversationEntity = _.cloneDeep(conversationEntity);

                            thisConversationEntity.name = conversationEntityName;
                            thisConversationEntity.model = entity.model;

                            const thisConversationField = _.cloneDeep(conversationField);
                            thisConversationField.type = conversationEntityName;

                            entities.push(thisConversationEntity);
                            fields.push(thisConversationField);
                        }
                    }
                }
            ]
        }
    },
    {name: "files", linkType: "OTM", norm: {fields: [
        {path: "fields", default: [], $p: {$f: function(fields, entity, entities) {
            const linkType = this.options.facet.linkType;
            const path = this.options.facet.path;
            const fileEntityName = "File" + entity.name;

            const thisFileEntity = _.cloneDeep(fileEntity);
            thisFileEntity.name = fileEntityName;
            thisFileEntity.model = entity.model;

            const thisFilesField = _.cloneDeep(filesField);
            thisFilesField.type = fileEntityName;
            if(linkType === "OTO"){
                thisFilesField.link = linkType;
                thisFilesField.path = path || "file";
                thisFilesField.nullable = true;
            } else {
                thisFilesField.path = path || "files";
            }

            entities.push(thisFileEntity);
            fields.push(thisFilesField);
        }}}
    ]}},
    {name: "date", norm: {fields: [
        {path: "fields", default: [], $p: {$u: function(fields) {
            return [_.cloneDeep(dateField)].concat(fields);
        }}}
    ]}}
];
